/**
 * Display formatting for the portal's market and company figures (home, operators, regions).
 * Lives in `~lib` rather than the portal so the shared UI primitives (`internal/ui`) — which
 * insights fragments render too — format a count or a delta exactly as the portal's pages do.
 *
 * Kept apart from the portal's `lib/inspector/format.ts` on purpose: the inspector formats token counts and quotes
 * them `12.3K` (en-US compact), whereas these are money and market figures, quoted the way the
 * British financial press does — `£6.6bn`, `$420m`, `48.2m adults` (en-GB compact). Every
 * formatter renders a missing or non-finite value as an em dash, so an absent figure never reads
 * as a zero.
 */

/** @desc What an absent figure renders as. */
export const NO_FIGURE = '—';

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const currencyFormatters = new Map<string, Intl.NumberFormat>();

const currencyFormatter = (currency: string, digits: number): Intl.NumberFormat => {
  const key = `${currency}:${digits}`;
  let formatter = currencyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: digits,
      // en-GB writes USD as `US$`; the portal's readers expect a bare `$`. Every other currency
      // keeps its disambiguating symbol (`A$`, `CA$`, `R$`), since a bare `$` would be ambiguous.
      currencyDisplay: currency === 'USD' ? 'narrowSymbol' : 'symbol',
    });
    currencyFormatters.set(key, formatter);
  }
  return formatter;
};

/** @desc A money amount, compact: `£6.6bn`, `$420m`, `€1.2bn`, `A$2.1bn`. */
export const formatCompactCurrency = (
  value: number | null | undefined,
  currency: string,
  maximumFractionDigits = 1,
): string =>
  isFiniteNumber(value)
    ? currencyFormatter(currency, maximumFractionDigits).format(value)
    : NO_FIGURE;

const compactNumber = new Intl.NumberFormat('en-GB', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** @desc A count, compact: `950`, `12.3k`, `48.2m`, `1.2bn`. */
export const formatCompactNumber = (value: number | null | undefined): string =>
  isFiniteNumber(value) ? compactNumber.format(value) : NO_FIGURE;

const wholeNumber = new Intl.NumberFormat('en-GB');

/** @desc A count with grouping separators: `1,234`. */
export const formatNumber = (value: number | null | undefined): string =>
  isFiniteNumber(value) ? wholeNumber.format(value) : NO_FIGURE;

// Percent formatters are keyed by their digits and sign display, like the currency ones above:
// building an `Intl.NumberFormat` per call is the expensive part, and a table formats hundreds.
const percentFormatters = new Map<string, Intl.NumberFormat>();

const percentFormatter = (digits: number, signed: boolean): Intl.NumberFormat => {
  const key = `${digits}:${signed ? 'signed' : 'plain'}`;
  let formatter = percentFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-GB', {
      style: 'percent',
      maximumFractionDigits: digits,
      ...(signed ? { signDisplay: 'exceptZero' as const } : {}),
    });
    percentFormatters.set(key, formatter);
  }
  return formatter;
};

/** @desc A fraction as a percentage: `0.124` → `12.4%`. Trailing zeros are dropped. */
export const formatPercent = (
  fraction: number | null | undefined,
  maximumFractionDigits = 1,
): string =>
  isFiniteNumber(fraction)
    ? percentFormatter(maximumFractionDigits, false).format(fraction)
    : NO_FIGURE;

/**
 * @desc A signed change as a percentage: `+12.4%`, `-3.1%`, `0%`. The sign is always spelt out
 * so the direction never depends on the chip's colour alone.
 */
export const formatDelta = (
  fraction: number | null | undefined,
  maximumFractionDigits = 1,
): string =>
  isFiniteNumber(fraction)
    ? percentFormatter(maximumFractionDigits, true).format(fraction)
    : NO_FIGURE;

/** @desc A signed percentage-point change, from a fraction: `0.012` → `+1.2 pp`. */
export const formatPointsDelta = (fraction: number | null | undefined): string => {
  if (!isFiniteNumber(fraction)) return NO_FIGURE;
  const points = Math.round(fraction * 1000) / 10;
  if (points === 0) return '0 pp';
  return `${points > 0 ? '+' : ''}${points} pp`;
};

/** @desc Whether a delta reads as up, down or flat — for the chip's colour and arrow. */
export const deltaTone = (fraction: number | null | undefined): 'up' | 'down' | 'flat' => {
  if (!isFiniteNumber(fraction) || Math.round(fraction * 1000) === 0) return 'flat';
  return fraction > 0 ? 'up' : 'down';
};

// Spelt out rather than taken from `Intl`: some ICU builds abbreviate September as `Sept` in
// en-GB and others as `Sep`, and the design (and every test) wants one spelling everywhere.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseDay = (value: string | Date): Date | null => {
  const at =
    value instanceof Date ? value : new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(at.getTime()) ? null : at;
};

/** @desc A calendar date, British order: `12 Oct 2026`. Accepts `YYYY-MM-DD` or a `Date`. */
export const formatDate = (value: string | Date | null | undefined): string => {
  if (value === null || value === undefined) return NO_FIGURE;
  const at = parseDay(value);
  if (!at) return NO_FIGURE;
  return `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
};

/** @desc A calendar month: `2026-09` → `Sep 2026`. */
export const formatMonth = (month: string | null | undefined): string => {
  if (!month) return NO_FIGURE;
  const at = parseDay(`${month.slice(0, 7)}-01`);
  return at ? `${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}` : NO_FIGURE;
};

const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });

/**
 * @desc A source line's or "Last updated" date, the month spelt out: "October 2026". Accepts a
 * `Date` or `YYYY-MM(-DD)`.
 */
export const sourceMonth = (at: Date | string): string =>
  MONTH_YEAR.format(typeof at === 'string' ? new Date(`${at.slice(0, 7)}-01T00:00:00Z`) : at);

/** @desc A file size: `420 KB`, `1.9 MB`. */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (!isFiniteNumber(bytes) || bytes < 0) return NO_FIGURE;
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
};
